<template>
    <div class="idp-game-targets">
        <div class="targets-header">
            <div class="targets-title">
                <i class="bi bi-bullseye"></i>
                <h3>Game Targets</h3>
            </div>
            <div class="targets-controls">
                <b-form-select class="quarter-select" v-model="selectedQuarter"
                    @change="$emit('quarter-change', selectedQuarter)" size="sm">
                    <b-form-select-option :value="null">Select quarter...</b-form-select-option>
                    <b-form-select-option value="1">Q1 (Jan – Mar)</b-form-select-option>
                    <b-form-select-option value="2">Q2 (Apr – Jun)</b-form-select-option>
                    <b-form-select-option value="3">Q3 (Jul – Sep)</b-form-select-option>
                    <b-form-select-option value="4">Q4 (Oct – Dec)</b-form-select-option>
                    <b-form-select-option value="all">Full Year</b-form-select-option>
                </b-form-select>
                <b-button v-if="canEdit" size="sm" variant="outline-success" @click="$emit('add')">
                    <i class="bi bi-plus-lg"></i>
                    <span class="btn-text">Add Target</span>
                </b-button>
            </div>
        </div>

        <div class="targets-grid" v-if="selectedQuarter">
            <!-- In Possession -->
            <div class="target-category in-possession">
                <div class="category-header">
                    <i class="bi bi-arrow-up-circle-fill"></i>
                    <h4>In Possession</h4>
                </div>
                <div class="category-body">
                    <div v-for="target in inPossessionTargets" :key="target.id" class="target-item">
                        <div class="target-content">
                            <img src="@/views/profile/img/target.svg" alt="" class="target-icon" />
                            <span v-html="target.target" class="target-text"></span>
                        </div>
                        <div class="target-footer">
                            <span :class="['status-badge', target.complete === 0 ? 'pending' : 'complete']">
                                {{ target.complete === 0 ? 'In Progress' : 'Complete' }}
                            </span>
                            <b-button v-if="canEdit" size="sm" variant="outline-warning" @click="$emit('edit', target)">
                                <i class="bi bi-pencil"></i>
                            </b-button>
                        </div>
                    </div>
                    <div v-if="inPossessionTargets.length === 0" class="no-targets">
                        <p>No in-possession targets set.</p>
                    </div>
                </div>
            </div>

            <!-- Out of Possession -->
            <div class="target-category out-of-possession">
                <div class="category-header">
                    <i class="bi bi-arrow-down-circle-fill"></i>
                    <h4>Out of Possession</h4>
                </div>
                <div class="category-body">
                    <div v-for="target in outOfPossessionTargets" :key="target.id" class="target-item">
                        <div class="target-content">
                            <img src="@/views/profile/img/target.svg" alt="" class="target-icon" />
                            <span v-html="target.target" class="target-text"></span>
                        </div>
                        <div class="target-footer">
                            <span :class="['status-badge', target.complete === 0 ? 'pending' : 'complete']">
                                {{ target.complete === 0 ? 'In Progress' : 'Complete' }}
                            </span>
                            <b-button v-if="canEdit" size="sm" variant="outline-warning" @click="$emit('edit', target)">
                                <i class="bi bi-pencil"></i>
                            </b-button>
                        </div>
                    </div>
                    <div v-if="outOfPossessionTargets.length === 0" class="no-targets">
                        <p>No out-of-possession targets set.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="select-quarter-prompt">
            <i class="bi bi-hand-index-thumb"></i>
            <p>Select a quarter to view targets</p>
        </div>

        <!-- Stats Summary -->
        <div class="targets-summary" v-if="selectedQuarter && targets.length > 0">
            <div class="summary-item">
                <span class="summary-value">{{ targets.length }}</span>
                <span class="summary-label">Total Targets</span>
            </div>
            <div class="summary-item complete">
                <span class="summary-value">{{ completedCount }}</span>
                <span class="summary-label">Completed</span>
            </div>
            <div class="summary-item pending">
                <span class="summary-value">{{ pendingCount }}</span>
                <span class="summary-label">In Progress</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'IDPGameTargets',
    props: {
        targets: {
            type: Array,
            default: () => []
        },
        initialQuarter: {
            type: String,
            default: null
        },
        canEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedQuarter: this.initialQuarter
        };
    },
    computed: {
        inPossessionTargets() {
            return this.targets.filter(t => t.possession === 'In possession');
        },
        outOfPossessionTargets() {
            return this.targets.filter(t => t.possession === 'Out of possession');
        },
        completedCount() {
            return this.targets.filter(t => t.complete !== 0).length;
        },
        pendingCount() {
            return this.targets.filter(t => t.complete === 0).length;
        }
    },
    watch: {
        initialQuarter(val) {
            this.selectedQuarter = val;
        }
    }
};
</script>

<style scoped lang="scss">
.idp-game-targets {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
}

.targets-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.targets-title {
    display: flex;
    align-items: center;
    gap: 0.625rem;

    i {
        font-size: 1.5rem;
        color: #f97316;
    }

    h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #1a202c;
    }
}

.targets-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .btn-text {
        margin-left: 0.25rem;
    }
}

.quarter-select {
    min-width: 160px;
    border-radius: 8px;
}

.targets-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.target-category {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
}

.category-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    color: #fff;
    font-weight: 600;

    i {
        font-size: 1.125rem;
    }

    h4 {
        margin: 0;
        font-size: 0.9375rem;
    }

    .in-possession & {
        background: linear-gradient(135deg, #22c55e, #16a34a);
    }

    .out-of-possession & {
        background: linear-gradient(135deg, #f97316, #ea580c);
    }
}

.category-body {
    padding: 1rem;
}

.target-item {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.875rem;
    margin-bottom: 0.75rem;

    &:last-child {
        margin-bottom: 0;
    }
}

.target-content {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    margin-bottom: 0.75rem;
}

.target-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
}

.target-text {
    font-size: 0.9375rem;
    color: #334155;
    line-height: 1.5;
}

.target-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    border-top: 1px dashed #e2e8f0;
}

.status-badge {
    padding: 0.25rem 0.625rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;

    &.pending {
        background: rgba(234, 179, 8, 0.15);
        color: #ca8a04;
    }

    &.complete {
        background: rgba(34, 197, 94, 0.15);
        color: #16a34a;
    }
}

.no-targets {
    text-align: center;
    padding: 1.5rem;
    color: #94a3b8;

    p {
        margin: 0;
        font-size: 0.875rem;
        font-style: italic;
    }
}

.select-quarter-prompt {
    text-align: center;
    padding: 2rem;
    color: #94a3b8;

    i {
        font-size: 2rem;
        display: block;
        margin-bottom: 0.5rem;
    }

    p {
        margin: 0;
        font-size: 0.9375rem;
    }
}

.targets-summary {
    display: flex;
    justify-content: center;
    gap: 2rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
}

.summary-item {
    text-align: center;

    .summary-value {
        display: block;
        font-size: 1.5rem;
        font-weight: 700;
        color: #334155;
    }

    .summary-label {
        font-size: 0.75rem;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.025em;
    }

    &.complete .summary-value {
        color: #16a34a;
    }

    &.pending .summary-value {
        color: #ca8a04;
    }
}

/* Mobile */
@media (max-width: 768px) {
    .idp-game-targets {
        padding: 1rem;
    }

    .targets-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .targets-controls {
        width: 100%;
        flex-direction: column;

        .quarter-select {
            width: 100%;
        }

        button {
            width: 100%;
            justify-content: center;
        }
    }

    .targets-grid {
        grid-template-columns: 1fr;
    }

    .targets-summary {
        gap: 1rem;
    }

    .summary-item .summary-value {
        font-size: 1.25rem;
    }
}

@media (max-width: 480px) {
    .category-header {
        padding: 0.75rem;

        h4 {
            font-size: 0.875rem;
        }
    }

    .category-body {
        padding: 0.75rem;
    }

    .target-item {
        padding: 0.75rem;
    }
}
</style>
